import { useEffect, useMemo, useState } from "react";
import type { z } from "zod";
import { addressSchema, launchpadApi, launchRequest, tokenDto, type LaunchToken } from "../../../api/launchpad";

const consumers = new Set<(kind: string, address?: string, data?: LaunchToken) => void>();
let stream: EventSource | undefined;
function listen(consumer: (kind: string, address?: string, data?: LaunchToken) => void) {
  consumers.add(consumer);
  if (!stream && typeof EventSource !== "undefined") {
    stream = new EventSource(launchpadApi + "/api/stream");
    const notify = (kind: string, address?: string, data?: LaunchToken) => consumers.forEach(fn => fn(kind, address, data));
    stream.onopen = () => notify("connected");
    stream.onerror = () => notify("offline");
    for (const kind of ["token:update", "trade", "holders:update", "chart:update"]) {
      stream.addEventListener(kind, event => {
        try {
          const data = JSON.parse((event as MessageEvent<string>).data);
          const token = kind === "token:update" ? tokenDto.safeParse(data) : addressSchema.safeParse(data?.token);
          if (token.success) notify(kind, (typeof token.data === "string" ? token.data : token.data.address).toLowerCase(), typeof token.data === "string" ? undefined : token.data);
        } catch { /* Ignore malformed stream events. */ }
      });
    }
  }
  return () => {
    consumers.delete(consumer);
    if (!consumers.size) { stream?.close(); stream = undefined; }
  };
}

export function useLaunchRead<T>(path: string | null, schema: z.ZodType<T>, channel?: string, address?: string, refreshKey?: string) {
  const requestKey = useMemo(() => ({ path, schema, refreshKey }), [path, schema, refreshKey]);
  const [state, setState] = useState<{ requestKey: object; data?: T; error?: unknown; offline?: boolean }>();
  useEffect(() => {
    if (!path || import.meta.env.VITE_APP_MODE === "DEMO") return;
    const request = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let running = false;
    let dirty = false;
    let offline = false;
    let latestDetail: LaunchToken | undefined;
    let eventGeneration = 0;
    let currentData: T | undefined;
    function schedule() {
      dirty = true;
      if (!timer && !running) timer = setTimeout(() => { timer = undefined; refresh(); }, 4_000);
    }
    function updateDetail(next: LaunchToken, preferIncomingOnTie: boolean, fromRest: boolean) {
      const previous = latestDetail;
      if (!previous) { latestDetail = next; return next; }
      const marketOrder = next.updatedBlock - previous.updatedBlock || next.updatedAt - previous.updatedAt;
      const metadataOrder = (next.metadataUpdatedAt ?? -1) - (previous.metadataUpdatedAt ?? -1);
      const market = marketOrder > 0 || marketOrder === 0 && preferIncomingOnTie ? next : previous;
      const metadata = metadataOrder > 0 || metadataOrder === 0 && fromRest && preferIncomingOnTie ? next : previous;
      latestDetail = { ...market, description: metadata.description, logoUrl: metadata.logoUrl, image: metadata.image, imageUrl: metadata.imageUrl,
        website: metadata.website, telegram: metadata.telegram, x: metadata.x, metadataUpdatedAt: metadata.metadataUpdatedAt };
      return latestDetail;
    }
    function detailFrom(data: T) {
      if (path !== "/tokens/" + address || !data || typeof data !== "object" || !("data" in data)) return undefined;
      const parsed = tokenDto.safeParse(data.data);
      return parsed.success && parsed.data.address.toLowerCase() === address ? parsed.data : undefined;
    }
    function withDetail(data: T, detail: LaunchToken) {
      const updated = schema.safeParse({ ...data, data: detail });
      return updated.success ? updated.data : data;
    }
    function refresh() {
      if (request.signal.aborted || !path) return;
      running = true;
      dirty = false;
      const requestGeneration = eventGeneration;
      void launchRequest(path, schema, { signal: request.signal }).then(data => {
        if (!request.signal.aborted) {
          const detail = detailFrom(data);
          currentData = detail ? withDetail(data, updateDetail(detail, requestGeneration === eventGeneration, true)) : data;
          setState({ requestKey, data: currentData, offline });
        }
      }, (error: unknown) => {
        if (!request.signal.aborted) setState(current => ({
          ...(current?.requestKey === requestKey ? current : {}), requestKey, error, offline,
        }));
      }).finally(() => {
        running = false;
        if (!request.signal.aborted && dirty) schedule();
      });
    }
    refresh();
    const unsubscribe = channel ? listen((kind, token, update) => {
      if (kind === "offline" || kind === "connected") {
        offline = kind === "offline";
        setState(current => current?.requestKey === requestKey ? { ...current, offline } : current);
        if (!offline) schedule();
      } else if (channel.split(" ").includes(kind) && (!address || token === address)) {
        if (update && path === "/tokens/" + address) {
          eventGeneration++;
          // Keep the event even during initial hydration. An older in-flight
          // response can supply the envelope without replacing its newer fields.
          const detail = updateDetail(update, true, false);
          if (currentData !== undefined) {
            const data = currentData = withDetail(currentData, detail);
            setState(current => current?.requestKey === requestKey ? { ...current, data } : current);
          }
        }
        if (kind === "chart:update" && !running) { clearTimeout(timer); timer = undefined; refresh(); }
        else schedule();
      }
    }) : undefined;
    const fallback = channel ? setInterval(schedule, 30_000) : undefined;
    return () => { request.abort(); clearTimeout(timer); clearInterval(fallback); unsubscribe?.(); };
  }, [path, schema, channel, address, requestKey]);
  return state?.requestKey === requestKey ? state : undefined;
}
